/**
 * Reviewed IMAP/SMTP presets for popular mail providers (provider id `imap`).
 *
 * Every endpoint here was checked against Mozilla's ISP database
 * (https://autoconfig.thunderbird.net/v1.1/<domain>) or the provider's own published
 * autoconfig file, and cross-checked with the provider's help pages; `source` and `reviewed`
 * record that. A calendar address is only included when the provider documents CalDAV for the
 * same password and the server answers on that host (redirects stay on the same host); otherwise
 * the preset has mail only. Changing an endpoint means reviewing the provider's documentation
 * again and updating `reviewed`.
 *
 * The connector service only ever sends a mailbox credential to the hosts of the preset the
 * person chose (or to servers the person typed in themselves). The `domains` list is used to
 * *suggest* a preset from the email address; a suggestion never sends anything anywhere.
 */
import type { MailSecurity } from './mail-servers.js';

export interface MailPresetEndpoint {
  readonly host: string;
  readonly port: number;
  readonly security: MailSecurity;
  /** What the server expects as the user name. Default: the full email address. */
  readonly username?: 'email' | 'localpart';
}

export type AppPasswordRule =
  /** The regular account password is refused by IMAP/SMTP; an app password is the only way. */
  | 'required'
  /** Needed when two-step verification is on (the regular password works otherwise). */
  | 'with-2fa'
  /** The mailbox password is used as is. */
  | 'no';

export interface MailPreset {
  readonly id: string;
  readonly label: string;
  /** Short name for account tags ("Yahoo"). */
  readonly shortLabel: string;
  /** Address domains this preset is suggested for. */
  readonly domains: readonly string[];
  readonly imap: MailPresetEndpoint;
  readonly smtp: MailPresetEndpoint;
  readonly caldavRoot?: string;
  readonly carddavRoot?: string;
  readonly appPassword: AppPasswordRule;
  /** What the provider calls the password to use ("app password", "secure mail key"). */
  readonly passwordName: string;
  /** Provider page explaining how to create the app password (or the mail-app settings). */
  readonly helpUrl: string;
  /** Provider page for a setting that must be turned on first ("allow IMAP access"). */
  readonly settingsUrl?: string;
  /** Short setup steps shown next to the form, e.g. "Turn on IMAP in Settings". */
  readonly setupNotes: readonly string[];
  readonly source: string;
  /** Date the endpoints were last reviewed (YYYY-MM-DD); stored with each account as its preset version. */
  readonly reviewed: string;
}

const REVIEWED = '2026-10-24';
const ISPDB = (d: string) => `Mozilla ISP database (autoconfig.thunderbird.net/v1.1/${d})`;

function preset(p: MailPreset): MailPreset {
  return Object.freeze({
    ...p,
    domains: Object.freeze([...p.domains]),
    setupNotes: Object.freeze([...p.setupNotes]),
    imap: Object.freeze({ ...p.imap }),
    smtp: Object.freeze({ ...p.smtp }),
  });
}

export const MAIL_PRESETS: readonly MailPreset[] = Object.freeze([
  preset({
    id: 'gmail',
    label: 'Gmail (app password)',
    shortLabel: 'Gmail',
    // Gmail addresses are offered Sign in with Google first; this preset is the alternative.
    // No calendar: Google CalDAV accepts OAuth only, not app passwords.
    domains: ['gmail.com', 'googlemail.com'],
    imap: { host: 'imap.gmail.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.gmail.com', port: 465, security: 'implicit-tls' },
    appPassword: 'required',
    passwordName: 'app password',
    helpUrl: 'https://support.google.com/accounts/answer/185833',
    setupNotes: [
      'Turn on 2-Step Verification for your Google Account, then create an app password.',
      'Work or school accounts often can’t create app passwords; use Sign in with Google instead.',
      'Calendar needs Sign in with Google; with an app password only mail is connected.',
    ],
    source: `${ISPDB('gmail.com')}; developers.google.com/workspace/gmail/imap/imap-smtp; support.google.com/accounts/answer/185833`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'yahoo',
    label: 'Yahoo Mail',
    shortLabel: 'Yahoo',
    domains: ['yahoo.com', 'ymail.com', 'rocketmail.com', 'myyahoo.com', 'yahoo.co.uk', 'yahoo.ca', 'yahoo.com.au', 'yahoo.co.nz', 'yahoo.co.in', 'yahoo.de', 'yahoo.fr', 'yahoo.it', 'yahoo.es', 'yahoo.se', 'yahoo.com.br', 'yahoo.com.mx', 'yahoo.com.ar'],
    imap: { host: 'imap.mail.yahoo.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.mail.yahoo.com', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://caldav.calendar.yahoo.com/',
    appPassword: 'required',
    passwordName: 'app password',
    helpUrl: 'https://help.yahoo.com/kb/SLN15241.html',
    setupNotes: ['In Yahoo Account Security, choose Generate app password and use it here.'],
    source: `${ISPDB('yahoo.com')}; help.yahoo.com/kb/SLN4075.html, SLN15241.html, SLN4707.html (CalDAV)`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'aol',
    label: 'AOL Mail',
    shortLabel: 'AOL',
    domains: ['aol.com', 'aim.com', 'aol.co.uk', 'aol.de', 'aol.fr', 'aol.it', 'aol.es', 'aol.se', 'aol.com.au', 'aol.co.nz', 'aol.com.ar', 'aol.com.br', 'aol.com.mx', 'netscape.net', 'compuserve.com', 'cs.com', 'wmconnect.com'],
    imap: { host: 'imap.aol.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.aol.com', port: 465, security: 'implicit-tls' },
    appPassword: 'required',
    passwordName: 'app password',
    helpUrl: 'https://help.aol.com/articles/create-and-manage-app-password',
    setupNotes: ['In AOL Account Security, choose Generate app password and use it here.'],
    source: `${ISPDB('aol.com')}; help.aol.com/articles/how-do-i-use-other-email-applications-to-send-and-receive-my-aol-mail`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'icloud',
    label: 'iCloud Mail',
    shortLabel: 'iCloud',
    domains: ['icloud.com', 'me.com', 'mac.com'],
    // Apple: the incoming user name is the part before @; outgoing uses the full address.
    // No calendar: Apple does not document a CalDAV address for app-specific passwords.
    imap: { host: 'imap.mail.me.com', port: 993, security: 'implicit-tls', username: 'localpart' },
    smtp: { host: 'smtp.mail.me.com', port: 587, security: 'starttls' },
    appPassword: 'required',
    passwordName: 'app-specific password',
    helpUrl: 'https://support.apple.com/en-us/102654',
    setupNotes: [
      'Turn on two-factor authentication for your Apple Account, then create an app-specific password at account.apple.com.',
      'For a custom email domain, enter your @icloud.com address as the username.',
    ],
    source: `${ISPDB('icloud.com')}; support.apple.com/en-us/102525, 102654`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'fastmail',
    label: 'Fastmail',
    shortLabel: 'Fastmail',
    domains: ['fastmail.com', 'fastmail.fm'],
    imap: { host: 'imap.fastmail.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.fastmail.com', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://caldav.fastmail.com/',
    carddavRoot: 'https://carddav.fastmail.com/',
    appPassword: 'required',
    passwordName: 'app password',
    helpUrl: 'https://www.fastmail.help/hc/en-us/articles/360058752854',
    setupNotes: ['In Settings → Privacy & Security → Manage app passwords and access, create an app password with access to mail and calendars.'],
    source: `Fastmail autoconfig (autoconfig.fastmail.com); ${ISPDB('messagingengine.com')}; fastmail.help/hc/en-us/articles/1500000278342`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'gmx',
    label: 'GMX Mail',
    shortLabel: 'GMX',
    domains: ['gmx.com', 'gmx.us', 'gmx.co.uk', 'gmx.ca', 'gmx.fr', 'gmx.es', 'gmx.tm'],
    imap: { host: 'imap.gmx.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'mail.gmx.com', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://caldav.gmx.com/',
    appPassword: 'with-2fa',
    passwordName: 'password',
    helpUrl: 'https://support.gmx.com/security/2fa/application-specific-passwords.html',
    settingsUrl: 'https://support.gmx.com/pop-imap/toggle.html',
    setupNotes: ['In GMX Settings → POP3 & IMAP, tick “Enable access to this account via POP3 and IMAP”. GMX turns it off again after a long time without use.'],
    source: `${ISPDB('gmx.com')}; support.gmx.com/pop-imap/imap/server.html, pop-imap/toggle.html, organizer/synchronizing/synchronizing-mac.html (CalDAV)`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'mailcom',
    label: 'mail.com',
    shortLabel: 'mail.com',
    domains: ['mail.com', 'email.com', 'usa.com', 'post.com', 'consultant.com', 'myself.com', 'dr.com', 'engineer.com', 'accountant.com', 'techie.com', 'writeme.com', 'iname.com', 'cheerful.com', 'mail.org', 'europe.com', 'asia.com'],
    imap: { host: 'imap.mail.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.mail.com', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://caldav.mail.com/',
    appPassword: 'with-2fa',
    passwordName: 'password',
    helpUrl: 'https://support.mail.com/pop-imap/toggle.html',
    settingsUrl: 'https://support.mail.com/pop-imap/toggle.html',
    setupNotes: ['IMAP access needs a mail.com Premium plan.', 'In mail.com Settings → POP3 & IMAP, tick “Enable access to this account via POP3 and IMAP”.'],
    source: `${ISPDB('mail.com')}; support.mail.com/pop-imap/imap/server.html, pop-imap/toggle.html, organizer/synchronizing/synchronizing-mac.html (CalDAV)`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'yandex',
    label: 'Yandex Mail',
    shortLabel: 'Yandex',
    // No calendar: Yandex CalDAV needs a separate "Calendar" app password.
    domains: ['yandex.com', 'yandex.ru', 'ya.ru', 'yandex.by', 'yandex.kz', 'yandex.ua', 'yandex.com.tr', 'narod.ru'],
    imap: { host: 'imap.yandex.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.yandex.com', port: 465, security: 'implicit-tls' },
    appPassword: 'required',
    passwordName: 'app password',
    helpUrl: 'https://yandex.com/support/mail/mail-clients/others.html',
    settingsUrl: 'https://yandex.com/support/mail/mail-clients/others.html',
    setupNotes: [
      'In Yandex Mail Settings → Email clients, turn on “From the imap.yandex.com server via IMAP” and “App passwords and OAuth tokens”.',
      'Create an app password of type Mail at id.yandex.com and use it here.',
    ],
    source: `${ISPDB('yandex.com')}; Yandex autoconfig (autoconfig.yandex.com); yandex.com/support/mail/mail-clients/others.html`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'namecheap',
    label: 'Namecheap Private Email',
    shortLabel: 'Private Email',
    domains: [],
    imap: { host: 'mail.privateemail.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'mail.privateemail.com', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://dav.privateemail.com/',
    carddavRoot: 'https://dav.privateemail.com/',
    appPassword: 'no',
    passwordName: 'mailbox password',
    helpUrl: 'https://www.namecheap.com/support/knowledgebase/article.aspx/1179/2175/general-private-email-configuration-for-mail-clients-and-mobile-devices',
    setupNotes: ['Use the mailbox password (or an application password made in Private Email), not your Namecheap account password.'],
    source: 'namecheap.com/support/knowledgebase articles 1179 and 10820 (CalDAV)',
    reviewed: REVIEWED,
  }),
  preset({
    id: 'godaddy',
    label: 'GoDaddy Professional Email',
    shortLabel: 'GoDaddy',
    // GoDaddy's Professional Email (powered by Titan) uses GoDaddy's own secureserver.net hosts.
    domains: [],
    imap: { host: 'imap.secureserver.net', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtpout.secureserver.net', port: 465, security: 'implicit-tls' },
    appPassword: 'no',
    passwordName: 'email password',
    helpUrl: 'https://www.godaddy.com/help/use-imap-settings-to-add-my-professional-email-powered-by-titan-to-a-client-32204',
    setupNotes: ['Use the password of this email address, not your GoDaddy account password.', 'GoDaddy mailboxes moved to Microsoft 365 connect with Sign in with Microsoft instead.'],
    source: `${ISPDB('secureserver.net')}; godaddy.com/help article 32204`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'titan',
    label: 'Titan Mail',
    shortLabel: 'Titan',
    domains: [],
    imap: { host: 'imap.titan.email', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.titan.email', port: 465, security: 'implicit-tls' },
    appPassword: 'with-2fa',
    passwordName: 'email password',
    helpUrl: 'https://support.titan.email/hc/en-us/articles/59899461438233-Application-Password-in-Titan-for-Third-Party-Clients',
    settingsUrl: 'https://support.titan.email/hc/en-us/articles/25005307786905-Enable-Titan-on-other-apps',
    setupNotes: ['In Titan, turn on “Enable Titan on Other Apps” first.', 'Mailboxes hosted in Titan’s EU region use other servers: choose Other and enter them.'],
    source: 'support.titan.email/hc/en-us/articles/900000215446, 25005307786905, 59899461438233',
    reviewed: REVIEWED,
  }),
  preset({
    id: 'hostinger',
    label: 'Hostinger Email',
    shortLabel: 'Hostinger',
    domains: [],
    imap: { host: 'imap.hostinger.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.hostinger.com', port: 465, security: 'implicit-tls' },
    appPassword: 'with-2fa',
    passwordName: 'email password',
    helpUrl: 'https://www.hostinger.com/support/1575756-how-to-get-email-account-configuration-details-for-hostinger-email/',
    setupNotes: ['Use the password of this email account, not your Hostinger login. If the mailbox uses two-factor authentication, create an app password.'],
    source: 'hostinger.com/support/1575756; docs.hostinger.com/emails/setup-devices',
    reviewed: REVIEWED,
  }),
  preset({
    id: 'ionos',
    label: 'IONOS Mail (US)',
    shortLabel: 'IONOS',
    domains: [],
    imap: { host: 'imap.ionos.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.ionos.com', port: 465, security: 'implicit-tls' },
    appPassword: 'no',
    passwordName: 'email password',
    helpUrl: 'https://www.ionos.com/help/email/general-topics/ionos-mail-server-details-for-imap-pop3-and-smtp/',
    setupNotes: ['Use the password of this email address, not your IONOS customer login.'],
    source: 'ionos.com/help/email/general-topics/ionos-mail-server-details-for-imap-pop3-and-smtp',
    reviewed: REVIEWED,
  }),
  preset({
    id: 'rackspace',
    label: 'Rackspace Email',
    shortLabel: 'Rackspace',
    domains: [],
    imap: { host: 'secure.emailsrvr.com', port: 993, security: 'implicit-tls' },
    smtp: { host: 'secure.emailsrvr.com', port: 465, security: 'implicit-tls' },
    appPassword: 'no',
    passwordName: 'mailbox password',
    helpUrl: 'https://docs.rackspace.com/docs/rackspace-email-settings',
    setupNotes: ['Use the mailbox password.'],
    source: `${ISPDB('emailsrvr.com')}; docs.rackspace.com/docs/rackspace-email-settings`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'mailboxorg',
    label: 'mailbox.org',
    shortLabel: 'mailbox.org',
    domains: ['mailbox.org'],
    imap: { host: 'imap.mailbox.org', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.mailbox.org', port: 465, security: 'implicit-tls' },
    caldavRoot: 'https://dav.mailbox.org/',
    carddavRoot: 'https://dav.mailbox.org/',
    appPassword: 'with-2fa',
    passwordName: 'password',
    helpUrl: 'https://kb.mailbox.org/en/private/security-and-privacy/application-passwords-for-external-programs/',
    setupNotes: ['Use your main mailbox.org address. If two-factor authentication is on, create an application password for mail and calendar.'],
    source: 'mailbox.org autoconfig (autoconfig.mailbox.org); kb.mailbox.org e-mail-configuration and CalDAV/CardDAV articles',
    reviewed: REVIEWED,
  }),
  preset({
    id: 'posteo',
    label: 'Posteo',
    shortLabel: 'Posteo',
    domains: ['posteo.de', 'posteo.net', 'posteo.at', 'posteo.ch', 'posteo.eu', 'posteo.com', 'posteo.org', 'posteo.us', 'posteo.uk', 'posteo.co.uk', 'posteo.nl', 'posteo.be', 'posteo.es', 'posteo.it', 'posteo.se', 'posteo.dk', 'posteo.ie', 'posteo.ca', 'posteo.jp'],
    imap: { host: 'posteo.de', port: 993, security: 'implicit-tls' },
    smtp: { host: 'posteo.de', port: 465, security: 'implicit-tls' },
    // Posteo's calendar server runs on port 8443 (contacts on 8843, which is not used here).
    caldavRoot: 'https://posteo.de:8443/',
    appPassword: 'with-2fa',
    passwordName: 'password',
    helpUrl: 'https://posteo.de/en/help/app-passwords',
    setupNotes: ['Use your Posteo address and password, or an app password made in Posteo’s settings.'],
    source: `${ISPDB('posteo.de')}; posteo.de/en/help (email client setup, app passwords, calendar synchronisation)`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'comcast',
    label: 'Xfinity (Comcast) Email',
    shortLabel: 'Xfinity',
    domains: ['comcast.net'],
    imap: { host: 'imap.comcast.net', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.comcast.net', port: 587, security: 'starttls' },
    appPassword: 'no',
    passwordName: 'Xfinity password',
    helpUrl: 'https://www.xfinity.com/support/articles/third-party-email-access',
    settingsUrl: 'https://www.xfinity.com/support/articles/third-party-email-access',
    setupNotes: ['In Xfinity Connect Settings → Security, tick Third Party Access Security first.'],
    source: `${ISPDB('comcast.net')}; xfinity.com/support/articles/email-client-programs-with-xfinity-email, third-party-email-access`,
    reviewed: REVIEWED,
  }),
  preset({
    id: 'att',
    label: 'AT&T Mail',
    shortLabel: 'AT&T',
    domains: ['att.net', 'sbcglobal.net', 'bellsouth.net', 'pacbell.net', 'prodigy.net', 'swbell.net', 'flash.net', 'ameritech.net', 'nvbell.net', 'snet.net', 'wans.net', 'currently.com'],
    imap: { host: 'imap.mail.att.net', port: 993, security: 'implicit-tls' },
    smtp: { host: 'smtp.mail.att.net', port: 465, security: 'implicit-tls' },
    appPassword: 'required',
    passwordName: 'secure mail key',
    helpUrl: 'https://www.att.com/support/article/email-support/KM1240308/',
    setupNotes: ['Create a secure mail key in your AT&T profile (Sign-in info) and use it here instead of your password.'],
    source: `${ISPDB('att.net')}; att.com/support/article/email-support/KM1010505, KM1240308`,
    reviewed: REVIEWED,
  }),
]);

const BY_ID = new Map(MAIL_PRESETS.map((p) => [p.id, p]));

export function mailPreset(id: string | null | undefined): MailPreset | null {
  return (id && BY_ID.get(id)) || null;
}

/** A reviewed preset suggested for an address domain (not Gmail: that is offered as an alternative to Google sign-in). */
export function presetForDomain(domain: string): MailPreset | null {
  const d = domain.trim().toLowerCase();
  return MAIL_PRESETS.find((p) => p.id !== 'gmail' && p.domains.includes(d)) ?? null;
}

/**
 * Providers whose mailboxes cannot be reached by a server with standard protocols. They are
 * explained plainly instead of being offered.
 */
export interface UnsupportedMailProvider {
  readonly id: string;
  readonly label: string;
  readonly domains: readonly string[];
  readonly reason: string;
}

export const UNSUPPORTED_MAIL_PROVIDERS: readonly UnsupportedMailProvider[] = Object.freeze([
  Object.freeze({
    id: 'proton',
    label: 'Proton Mail',
    domains: Object.freeze(['proton.me', 'protonmail.com', 'protonmail.ch', 'pm.me']),
    reason:
      'Proton Mail can’t be connected. Its mailboxes are only reachable through Proton’s own apps or Proton Mail Bridge running on your own computer, and DeskWorks One connects from its server.',
  }),
  Object.freeze({
    id: 'tuta',
    label: 'Tuta (Tutanota)',
    domains: Object.freeze(['tuta.com', 'tuta.io', 'tutanota.com', 'tutanota.de', 'tutamail.com', 'keemail.me']),
    reason: 'Tuta mail can’t be connected. Tuta doesn’t offer IMAP or SMTP access for other email apps.',
  }),
  Object.freeze({
    id: 'hey',
    label: 'HEY',
    domains: Object.freeze(['hey.com']),
    reason: 'HEY mail can’t be connected. HEY doesn’t offer IMAP or SMTP access for other email apps.',
  }),
]);

/** Guidance shown with the password field for a preset. */
export function presetPasswordHint(p: MailPreset): string {
  switch (p.appPassword) {
    case 'required':
      return `${p.label} needs ${withArticle(p.passwordName)} for other apps — your regular password won’t work here.`;
    case 'with-2fa':
      return `Use your ${p.label} password. If two-step verification is on, create an app password and use that instead.`;
    case 'no':
      return `Use your ${p.passwordName}.`;
  }
}

/** "an app password", "a secure mail key". */
function withArticle(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;
}
